/**
 * Home-screen "Coming Up" section. Full-width one-line rows for the two things
 * a resident most often needs off the home screen: where their balance stands,
 * and what's next on the community calendar.
 *
 * The section is deliberately data-driven and renders NOTHING (not even the
 * header) when it has no rows — the no-scroll home layout gives that space back
 * to the planner, which stretches to fill it.
 *
 * Labels are formatted by the caller (`dueDateLabel` / `dateLabel`) so this
 * component stays a pure presenter and both the mobile and desktop home views
 * can feed it the same shapes.
 */
import React from 'react';
import {
  View,
  StyleSheet,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { Text } from '@/components/common/text';
import { GlassCard } from '../GlassCard';
import { SectionHeader } from '../SectionHeader';
import { useThemeColors, useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useFontScale } from '@/hooks/shared/useFontScale';
import { useRadius } from '@/hooks/shared/useRadius';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { formatCurrency } from '@/utils/common/formatting';
import type { BalanceStatus } from '@/features/payments/common/helpers';

/**
 * The resident's lease balance. `amount` is the raw balance, not a label: it is
 * shown as a magnitude, and `status` picks the ink and, for a credit, the row's
 * title. The caller decides the status (via `getBalanceStatus`, which reads the
 * day of the month) so this stays a pure presenter.
 */
export interface ComingUpRentDue {
  amount: number;
  /** Pre-formatted short date, e.g. "Sep 1". Omitted when unknown. */
  dueDateLabel?: string;
  /** Owed, past due, or in the resident's favor. Defaults to owed. */
  status?: BalanceStatus;
}

/** The single event surfaced on the home screen. */
export interface ComingUpNextEvent {
  title: string;
  /** Pre-formatted short date, e.g. "Sep 5". Omitted when unknown. */
  dateLabel?: string;
}

interface ComingUpSectionProps {
  rentDue?: ComingUpRentDue | null;
  onRentDuePress?: () => void;
  nextEvent?: ComingUpNextEvent | null;
  onNextEventPress?: () => void;
  /**
   * Tightens the section header's vertical padding — for the no-scroll home,
   * where header whitespace is height the planner does not get. Off by default.
   */
  denseHeader?: boolean;
  style?: StyleProp<ViewStyle>;
}

type IconComponent = React.ComponentType<{
  width?: number;
  height?: number;
  color?: string;
}>;

const ICON_SIZE = 16;
const ICON_TILE_SIZE = 32;

interface ComingUpRowProps {
  Icon: IconComponent;
  title: string;
  /** Emphasized right-side value (the rent amount). */
  amountText?: string;
  /** Which ink the amount takes. Only the balance row sets it. */
  amountTone?: BalanceStatus;
  /** Muted right-side value (a short date label). */
  metaText?: string;
  accessibilityLabel: string;
  onPress?: () => void;
}

const ComingUpRow: React.FC<ComingUpRowProps> = React.memo(({
  Icon,
  title,
  amountText,
  amountTone = 'normal',
  metaText,
  accessibilityLabel,
  onPress,
}) => {
  const { colors } = useThemeColors();
  const type = useTypography();
  const space = useSpacing();
  const radius = useRadius();
  const { isLargeText } = useFontScale();
  const styles = useThemedStyles(({ colors: themeColors, semantic }) =>
    StyleSheet.create({
      row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: space.md,
        paddingHorizontal: space.lg,
      },
      iconTile: {
        width: ICON_TILE_SIZE,
        height: ICON_TILE_SIZE,
        borderRadius: radius.md,
        borderWidth: 1,
        backgroundColor: themeColors.activityIcon.bg,
        borderColor: themeColors.activityIcon.border,
        alignItems: 'center',
        justifyContent: 'center',
      },
      // Title + meta live in their own column so the meta can drop under the
      // title at large text; at 1x it is a row with the same gap the outer
      // row used to put between them, so nothing moves.
      textColumn: {
        flex: 1,
        minWidth: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: space.md,
      },
      textColumnStacked: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
      },
      title: {
        ...type.cardTitle,
        color: themeColors.text.primary,
        // Takes the leftover width so a long event name ellipsizes instead of
        // pushing the amount / date off the row.
        flex: 1,
      },
      titleStacked: {
        flex: 0,
        alignSelf: 'stretch',
      },
      meta: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: space.sm,
        // May shrink: at large text the amount + date wrap under the title
        // instead of squeezing it to nothing. Wrapping is inert at 1x (the
        // meta only wraps when it alone exceeds the column); at 1.5x on a
        // 320pt phone it keeps the date from running across the chevron.
        flexShrink: 1,
        flexWrap: 'wrap',
      },
      amount: {
        ...type.cardTitle,
        fontWeight: '700',
        color: themeColors.text.primary,
      },
      // 14px bold is normal-size text, so the 4.5:1 floor applies. The plain
      // status.error / status.success indicators miss it on the light card
      // (4.13:1 and 2.78:1); the *Text inks clear it in both themes.
      amountOverdue: {
        color: semantic.status.errorText,
      },
      amountCredit: {
        color: semantic.status.successText,
      },
      metaText: {
        ...type.caption,
        // secondary, not muted: muted measures 4.48:1 on the light card, under
        // the 4.5:1 AA floor for this 12px caption (same call the planner's
        // empty-state hint and TodoRow's date label make).
        color: themeColors.text.secondary,
      },
    }),
  );

  return (
    // The card is the pressable when there is somewhere to go (desktop hover:
    // border, shadow, lift; 0.7 press dip) and a plain card otherwise — still
    // a disabled button to assistive tech, as the wrapper used to report.
    <GlassCard
      interactive={!!onPress}
      onPress={onPress}
      borderRadius={radius.lg}
      padding={space.md}
      style={styles.row}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled: !onPress }}
    >
      <View style={styles.iconTile}>
        <Icon
          width={ICON_SIZE}
          height={ICON_SIZE}
          color={colors.text.tertiary}
        />
      </View>
      <View style={[styles.textColumn, isLargeText && styles.textColumnStacked]}>
        <Text
          style={[styles.title, isLargeText && styles.titleStacked]}
          numberOfLines={isLargeText ? 2 : 1}
          ellipsizeMode="tail"
        >
          {title}
        </Text>
        {(amountText || metaText) && (
          <View style={styles.meta}>
            {amountText ? (
              <Text
                style={[
                  styles.amount,
                  amountTone === 'overdue' && styles.amountOverdue,
                  amountTone === 'credit' && styles.amountCredit,
                ]}
              >
                {amountText}
              </Text>
            ) : null}
            {metaText ? <Text style={styles.metaText}>{metaText}</Text> : null}
          </View>
        )}
      </View>
      <RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT
        width={ICON_SIZE}
        height={ICON_SIZE}
        color={colors.text.muted}
      />
    </GlassCard>
  );
});

ComingUpRow.displayName = 'ComingUpRow';

export const ComingUpSection: React.FC<ComingUpSectionProps> = React.memo(({
  rentDue,
  onRentDuePress,
  nextEvent,
  onNextEventPress,
  denseHeader = false,
  style,
}) => {
  const space = useSpacing();
  const styles = useThemedStyles(() =>
    StyleSheet.create({
      rows: {
        gap: space.md,
      },
    }),
  );

  // Contribute zero height when there is nothing to show — the header must not
  // render on its own, otherwise the planner below inherits a phantom gap.
  if (!rentDue && !nextEvent) return null;

  // Matches PaymentBalanceDueCard: the balance is displayed as a magnitude.
  const amountText = rentDue ? formatCurrency(Math.abs(rentDue.amount)) : undefined;
  const rentStatus = rentDue?.status ?? 'normal';
  // A credit is money in the resident's favor, so "Rent due" would misread.
  const rentTitle = rentStatus === 'credit' ? 'Account credit' : 'Rent due';

  return (
    <View style={style}>
      <SectionHeader title="Coming Up" dense={denseHeader} />
      <View style={styles.rows}>
        {rentDue ? (
          <ComingUpRow
            Icon={RESIDENTS_SVG_COMPONENTS.CREDIT_CARD}
            title={rentTitle}
            amountText={amountText}
            amountTone={rentStatus}
            metaText={rentDue.dueDateLabel}
            accessibilityLabel={`${rentTitle} ${amountText}${
              rentDue.dueDateLabel ? `, due ${rentDue.dueDateLabel}` : ''
            }`}
            onPress={onRentDuePress}
          />
        ) : null}
        {nextEvent ? (
          <ComingUpRow
            Icon={RESIDENTS_SVG_COMPONENTS.CALENDAR}
            title={nextEvent.title}
            metaText={nextEvent.dateLabel}
            accessibilityLabel={`${nextEvent.title}${
              nextEvent.dateLabel ? `, ${nextEvent.dateLabel}` : ''
            }`}
            onPress={onNextEventPress}
          />
        ) : null}
      </View>
    </View>
  );
});

ComingUpSection.displayName = 'ComingUpSection';
